import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HR } from '../components/HR';
import { Button } from '../components/Button';
import { Tag } from '../components/Tag';
import { api, type ApiOrganisation } from '../services/api';
import styles from './SettingsScreen.module.css';

function downloadCsv(filename: string, csvText: string) {
  const blob = new Blob([csvText], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function SettingsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [feePerTicket, setFeePerTicket] = useState('0.00');
  const [organisations, setOrganisations] = useState<ApiOrganisation[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [conciergeMessage, setConciergeMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [savingFee, setSavingFee] = useState(false);

  useEffect(() => {
    api.settings.communityFee()
      .then(data => setFeePerTicket(data.fee))
      .catch(() => console.error('Failed to fetch community fee'));
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    setOrgsLoading(true);
    api.organisations.list()
      .then(setOrganisations)
      .catch(() => setOrganisations([]))
      .finally(() => setOrgsLoading(false));
  }, [user?.role]);

  const saveFee = async () => {
    setSavingFee(true);
    setActionError(null);
    try {
      const result = await api.settings.updateCommunityFee(feePerTicket);
      setFeePerTicket(result.fee);
    } catch (error) {
      console.error('Failed to save community fee:', error);
      setActionError('Failed to save community fee. Please try again.');
    } finally {
      setSavingFee(false);
    }
  };

  const toggleConcierge = async (org: ApiOrganisation, next: boolean) => {
    setTogglingId(org.id);
    setActionError(null);
    setConciergeMessage(null);
    try {
      const updated = await api.organisations.update(org.id, { isConcierge: next });
      setOrganisations(prev => prev.map(o => (o.id === updated.id ? { ...o, is_concierge: updated.is_concierge } : o)));
      setConciergeMessage(`${org.name} is now ${next ? 'running concierge' : 'on the standard tier'}.`);
    } catch (error) {
      console.error('Failed to toggle concierge:', error);
      setActionError('Failed to update organisation. Please try again.');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      {actionError && (
        <div className={styles.errorBanner}>
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className={styles.errorClose}>×</button>
        </div>
      )}
      <div className={styles.backButtonContainer}>
        <button
          onClick={() => navigate(-1)}
          className={styles.backButton}
        >
          ← Back
        </button>
      </div>

      <div className={styles.headerContainer}>
        <h2 className={styles.header}>Settings</h2>
        <p className={styles.subtitle}>
          Admin controls for fees and concierge tier.
        </p>
      </div>

      <HR />

      {/* Community Fee */}
      {user?.role === 'admin' && (
        <div className={styles.section}>
        <div className={styles.sectionLabel}>Community Fee</div>
        <div className={styles.sectionDescription}>
          Per-ticket fee collected by OnIsland to sustain the platform.
        </div>
        <div className={styles.feeRow}>
          <span className={styles.feePrefix}>$</span>
          <input
            type="number"
            value={feePerTicket}
            onChange={e => setFeePerTicket(e.target.value)}
            className={styles.feeInput}
            min="0"
            step="0.01"
          />
          <span className={styles.feeSuffix}>per ticket</span>
        </div>
        <div className={styles.feeActions} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
          <Button
            style={{ justifyContent: 'center' }}
            disabled={savingFee}
            onClick={saveFee}
          >
            {savingFee ? 'Saving…' : 'Save'}
          </Button>
          <Tag variant={parseFloat(feePerTicket) === 0 ? 'accent' : 'neutral'}>
            {parseFloat(feePerTicket) === 0 ? 'Waived at launch' : `Active: $${feePerTicket}/ticket`}
          </Tag>
        </div>
        <p className={styles.feeNote}>
          Waived while we get going — OnIsland takes nothing per ticket right now.
        </p>
        </div>
      )}

      <HR />

      {/* Concierge Tier */}
      {user?.role === 'admin' && (
        <div className={styles.section}>
        <div className={styles.sectionLabel}>Concierge Tier</div>
        <div className={styles.sectionDescription}>
          OnIsland runs an organisation's events under its own Ticket Tailor account. Full-service booking and management.
        </div>
        {conciergeMessage && (
          <div className={styles.successMessage}>
            {conciergeMessage}
          </div>
        )}
        {orgsLoading ? (
          <div className={styles.loadingState}>Loading organisations...</div>
        ) : organisations.length === 0 ? (
          <div className={styles.loadingState}>No organisations yet.</div>
        ) : (
          <div className={styles.orgList}>
            {organisations.map(org => (
              <div key={org.id} className={styles.orgRow}>
                <div>
                  <div className={styles.orgName}>{org.name}</div>
                  <div className={styles.orgTagContainer}>
                    <Tag variant={org.is_concierge ? 'accent' : 'neutral'}>
                      {org.is_concierge ? 'Concierge' : 'Standard'}
                    </Tag>
                  </div>
                </div>
                <div className={styles.orgActions}>
                  {org.is_concierge ? (
                    <Button
                      className={styles.cancelConcierge}
                      style={{ justifyContent: 'center' }}
                      disabled={togglingId === org.id}
                      onClick={() => toggleConcierge(org, false)}
                    >
                      {togglingId === org.id ? 'Updating…' : 'Cancel'}
                    </Button>
                  ) : (
                    <Button
                      style={{ justifyContent: 'center' }}
                      disabled={togglingId === org.id}
                      onClick={() => toggleConcierge(org, true)}
                    >
                      {togglingId === org.id ? 'Updating…' : 'Activate'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      )}

      <HR />

      {/* Organisations (admin only) */}
      {user?.role === 'admin' && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Organisations</div>
          <div className={styles.sectionDescription}>
            Manage organisations that run events on the platform.
          </div>
          <Button
            style={{ justifyContent: 'center' }}
            onClick={() => navigate('/organisations')}
          >
            View Organisations
          </Button>
        </div>
      )}

      <HR />

      {/* Reports (admin only) */}
      {user?.role === 'admin' && (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Reports</div>
          <div className={styles.sectionDescription}>
            Download CSV reports for events and volunteers.
          </div>
          <div className={styles.reportButtons}>
            <Button
              className={styles.centerFlex}
              style={{ justifyContent: 'center' }}
              onClick={async () => {
                setActionError(null);
                try {
                  const csv = await api.reports.eventsCsv();
                  downloadCsv('events-report.csv', csv);
                } catch (error) {
                  console.error('Failed to download events report:', error);
                  setActionError('Failed to download events report. Please try again.');
                }
              }}
            >
              Events Report
            </Button>
            <Button
              className={styles.centerFlex}
              style={{ justifyContent: 'center' }}
              onClick={async () => {
                setActionError(null);
                try {
                  const csv = await api.reports.volunteersCsv();
                  downloadCsv('volunteers-report.csv', csv);
                } catch (error) {
                  console.error('Failed to download volunteers report:', error);
                  setActionError('Failed to download volunteers report. Please try again.');
                }
              }}
            >
              Volunteers Report
            </Button>
          </div>
        </div>
      )}

      <HR />

      {/* Ticket Tailor Integration */}
      <div className={styles.section}>
        <div className={styles.sectionLabel}>Ticket Tailor API</div>
        <div className={styles.apiRow}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ec3013" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <span className={styles.apiStatus}>API Key: not configured</span>
        </div>
        <p className={styles.apiNote}>
          Server-side only. Never exposed to client.
        </p>
        {user?.role === 'admin' && (
          <div className={styles.syncContainer}>
            <Button
              style={{ justifyContent: 'center' }}
              onClick={async () => {
                setActionError(null);
                setSyncMessage(null);
                try {
                  const result = await api.events.sync();
                  if (result.success) {
                    setSyncMessage(`Synced ${result.synced} events`);
                  }
                } catch (error) {
                  console.error('Failed to sync events:', error);
                  setActionError('Failed to sync events. Please try again.');
                }
              }}
            >
              Sync Events from Ticket Tailor
            </Button>
            {syncMessage && <span className={styles.syncMessage}>{syncMessage}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
